/**
 * @fileoverview Input/output schemas for the story-dna-extractor-v2 agent.
 *
 * This agent extracts the core "DNA" of a story - fundamental architectural
 * components like plot type, POV, genre, and setting.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { PLOT_TYPES, POV_TYPES, STORY_TYPES, STORY_SETTINGS, SUPPORTED_LANGUAGES } from '@/domains/story-planning/constants';
import { getGenreCatalog } from '@/domains/style-guides/genre-helpers';

// Dynamic enum values
const allGenreTemplateIds = getGenreCatalog().map(g => g.value);
const allStoryTypeValues = STORY_TYPES.map(t => t.value);
const allStorySettings = STORY_SETTINGS.map(s => s.value);
const allLanguages = SUPPORTED_LANGUAGES.map(l => l.value);

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

export const InputSchema = BaseFlowInputSchema.extend({
  textInput: z.string().describe('The full text content of the document to analyze.'),
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  storyType: z.enum(allStoryTypeValues as [string, ...string[]]).describe('The most fitting story type based on the text\'s length and scope.'),
  plotType: z.enum(PLOT_TYPES as [string, ...string[]]).describe('The most fitting plot type from the allowed list.'),
  pov: z.enum(POV_TYPES as [string, ...string[]]).describe('The most fitting narrative point of view from the allowed list.'),
  genres: z.array(z.enum(allGenreTemplateIds as [string, ...string[]])).describe('An array of up to two genre template IDs that best fit the story.'),
  storySetting: z.enum(allStorySettings as [string, ...string[]]).describe('The most fitting story setting from the allowed list.'),
  language: z.enum(allLanguages as [string, ...string[]]).describe('The language of the text, detected from the content.'),
  justification: z.string().describe('A brief justification for the chosen DNA components.'),
});

export type Output = z.infer<typeof OutputSchema>;
